/**
 * Response shapes of the Studio API, mirrored from apps/api/src/routes/* and the @studio/db query row types
 * (IslandWindowRow, BreakoutRow, IslandDetail, GenreWindowRow). They are copied rather than imported because
 * @studio/db is a Node package; keep them in step with those files.
 */

export type Day = string;
export type WindowDays = 1 | 7 | 30 | 90;
export const WINDOW_OPTIONS: readonly WindowDays[] = [1, 7, 30, 90];

export type Readiness = { status: 'ready' } | { status: 'collecting'; have: number; need: number };

export interface SnapshotEnvelope {
  as_of: Day | null;
  stale: boolean;
  readiness: Readiness;
}

export interface IslandWindowRow {
  code: string;
  title: string | null;
  creatorCode: string | null;
  peakCcuMax: number | null;
  avgDailyUniques: number | null;
  minutesTotal: number | null;
  minutesEst: number | null;
  minutesPerPlayerDay: number | null;
  d1Wavg: number | null;
  d7Wavg: number | null;
  wowPct: number | null;
  daysCollected: number;
  daysPresent: number;
  daysNotCollected: number;
  coverageRatio: number;
}

export type TopSort = 'peak' | 'uniques' | 'minutes' | 'minutes_per_player' | 'momentum';

export interface TopIslandsResponse extends SnapshotEnvelope {
  window: WindowDays;
  momentum_readiness: Readiness;
  sort: TopSort;
  filters: { tag: string | null; genre: string | null };
  items: IslandWindowRow[];
}

export interface BreakoutRow extends IslandWindowRow {
  firstSeen: Day;
  ageDays: number;
}

export interface BreakoutsResponse extends SnapshotEnvelope {
  window: 7;
  thresholds: { max_age_days: number; min_wow_pct: number; min_peak: number };
  items: BreakoutRow[];
  age_unknown: IslandWindowRow[];
}

export type ScoreComponentName = 'demand' | 'growth' | 'supply' | 'concentration' | 'newEntrant';

export interface ScoreComponent {
  name: ScoreComponentName;
  raw: number | null;
  normalized: number | null;
  weight: number;
  contribution: number;
}

export type GenreDimension = 'tag' | 'epic_genre';

export interface GenreItem {
  key: string;
  status: 'scored' | 'too_small';
  score: number | null;
  components: ScoreComponent[];
  excluded: ScoreComponentName[];
  crowded_at_top: boolean;
  weights_version?: string;
  metrics: {
    demand_minutes: number | null;
    growth_pct: number | null;
    supply_active: number;
    top5_share: number | null;
    quality_bar_d7: number | null;
    new_entrants: number;
    new_entrants_hit: number;
    new_entrant_success: number | null;
  };
  coverage: {
    islands_in_demand: number;
    islands_low_coverage: number;
    comparable_islands: number;
    coverage_avg: number | null;
  };
  provisional: boolean;
}

export interface GenresResponse extends SnapshotEnvelope {
  dimension: GenreDimension;
  window: WindowDays;
  min_genre_active: number;
  items: GenreItem[];
}

export interface GenreDemandPoint {
  day: Day;
  minutes: number | null;
  collectedIslands: number;
  members: number;
  collected_share: number | null;
  provisional: boolean;
}

export interface GenreDetailResponse extends SnapshotEnvelope {
  dimension: GenreDimension;
  window: WindowDays;
  item: GenreItem;
  top_islands: IslandWindowRow[];
  demand_series: GenreDemandPoint[];
}

export type DayState = 'data' | 'no_data' | 'not_collected';

export interface SeriesPoint {
  day: Day;
  state: DayState;
  peakCcu?: number | null;
  uniquePlayers?: number | null;
  minutesPlayed?: number | null;
  retentionD1?: number | null;
  retentionD7?: number | null;
}

export interface IslandDetailResponse {
  as_of: Day | null;
  stale: boolean;
  island: {
    code: string;
    title: string | null;
    creatorCode: string | null;
    category: string | null;
    createdIn: string | null;
    firstSeen: Day;
    firstSeenConfident: boolean;
    lastSeen: Day;
    removedAt: Day | null;
    coverageFrom: Day | null;
    coveredThrough: Day | null;
    dormantSince: Day | null;
  };
  tags: string[];
  keys: Array<{ dimension: string; key: string }>;
  gaps: Array<{ from: Day; to: Day; reason: string }>;
  windows: Array<IslandWindowRow & { window: WindowDays; as_of: Day | null; readiness: Readiness }>;
  series: { from: Day; to: Day; points: SeriesPoint[] };
  similar: Array<{ code: string; title: string | null; sharedTags: number; titleSimilarity: number }>;
}

export interface FreshnessResponse {
  today: Day;
  as_of: Day | null;
  stale: boolean;
  history_start: Day | null;
  published_at: string | null;
  last_ingest: { runDate: Day; attempt: number; status: string; finishedAt: string | null } | null;
  last_rollup: { runDate: Day; status: string; asOf: Day | null; finishedAt: string | null } | null;
}

export interface WatchlistResponse {
  code: string;
  created: boolean;
  known_island: boolean;
}
